import { privateApi } from 'services';
import { UpdateUser, UpdateUserDefaultRole, UpdateUserDisabledProperty, User, ZendeskToken } from './types';
import { privateApiWithRouteLeavingCancelation } from '../ApiService/ApiServiceWithRouteLeavingCancelation';
import queryString from 'query-string';
import { mapIdsToQueryString } from 'lib/utils/url.utils';

const fetchUser = async (userId: string): Promise<User> => privateApi.get<User>(`users/${userId}`);

const makeFetchUsersRequest = (path: string): Promise<User[]> => privateApi.get<User[]>(path);

const fetchUsers = async (userIds?: string[]): Promise<User[]> => {
    if (userIds) {
        return makeFetchUsersRequest(`users?${mapIdsToQueryString(userIds).toString()}`);
    }
    return makeFetchUsersRequest('users');
};

const fetchZendeskToken = async (userId: string): Promise<ZendeskToken> =>
    privateApi.post(`users/${userId}/zendesk-end-user-authentication-token`);

const updateUser = async (user: UpdateUser): Promise<User> => privateApi.put<User>(`users/${user.id}`, user);

const updateUserDefaultRole = async (userId: string, payload: UpdateUserDefaultRole): Promise<User> =>
    privateApi.put<User>(`users/${userId}/default-role`, payload);

const updateUserDisabledProperty = async (userId: string, payload: UpdateUserDisabledProperty): Promise<User> =>
    privateApi.put<User>(`users/${userId}/disabled`, payload);

const resendActivationEmail = async (userId: string): Promise<void> =>
    privateApi.post<void>(`users/${userId}/resend-activation-email`);

const requestPasswordReset = async (userId: string): Promise<void> =>
    privateApi.post<void>(`users/${userId}/request-password-reset`);

const updateUserAvatar = async (userId: string, formData: FormData): Promise<User> =>
    privateApi.postMultipart<User>(`users/${userId}/avatar`, formData);

const addEmployee = async (email: string): Promise<User> => privateApi.post<User>('users/employees', { email: email });

export const UserGateway = {
    fetchUser,
    fetchUsers,
    fetchZendeskToken,
    updateUser,
    updateUserDefaultRole,
    updateUserDisabledProperty,
    updateUserAvatar,
    addEmployee,
    resendActivationEmail,
    requestPasswordReset,
};

const cancelableFetchUser = async (userId: string): Promise<User> =>
    privateApiWithRouteLeavingCancelation.getWithAbortControl<User>(`users/${userId}`);

const makeCancelableFetchUsersRequest = (path: string): Promise<User[]> =>
    privateApiWithRouteLeavingCancelation.getWithAbortControl<User[]>(path);

const cancelableFetchUsers = async (
    userIds?: string[],
    businessAccountId?: string,
    employee?: boolean,
): Promise<User[]> => {
    const params = queryString.stringify({
        id: userIds,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'memberships.businessAccount': businessAccountId,
        employee,
    });
    return makeCancelableFetchUsersRequest(`users${params ? `?${params}` : ''}`);
};

export const CancelableUserGateway = {
    cancelableFetchUser,
    cancelableFetchUsers,
};
